// Progressive loader — orchestrates "background prefetch during intro"
// so heavy UI / lobby modules arrive warm in the cache before the
// player needs them. Day 1 ships the scaffold; Day 2 wires the actual
// dynamic import() calls into the consumer sites.
//
// Usage:
//   import { preload, ready } from './boot/progressive_loader';
//   // kick off at team-picker mount:
//   preload('lobby', () => import('../lobby/cosmetic_shop'));
//   // gate match entry on readiness:
//   await ready('lobby');
//
// Keys are semantic groups (e.g. 'lobby', 'ui-heavy', 'match-heavy'),
// not file paths — the loader stays vendor-agnostic.

type Key = 'lobby' | 'ui-heavy' | 'match-heavy' | 'theme' | 'recap' | 'bounty' | 'emotes' | 'boss-cine';

const registry = new Map<Key, Promise<unknown>>();
const completed = new Set<Key>();

// Starts (or retrieves) a preload for `key`. If already started, the
// existing promise is returned — the loader is idempotent. The promise
// resolves after the dynamic import() settles (success OR failure:
// failures are caught and logged so one dead module never blocks the
// rest of the loader pipeline).
export function preload<T>(key: Key, loader: () => Promise<T>): Promise<T> {
  const existing = registry.get(key);
  if (existing) return existing as Promise<T>;
  const p = loader()
    .then((mod) => {
      completed.add(key);
      return mod;
    })
    .catch((err) => {
      console.warn('[boot] preload failed:', key, err);
      completed.add(key);
      return undefined as unknown as T;
    });
  registry.set(key, p);
  return p;
}

// Awaits a previously-kicked-off preload. If the key was never started,
// resolves immediately (no-op). Callers should still explicitly
// `preload()` before awaiting — this is a gate, not an implicit fetcher.
export async function ready(key: Key): Promise<void> {
  const p = registry.get(key);
  if (p) await p;
}

// Awaits all preloads currently registered. Useful for a hard gate
// before entering a match.
export async function readyAll(): Promise<void> {
  await Promise.all([...registry.values()]);
}

// Sync check — is this module already resident? Useful for conditional
// rendering (e.g. show "loading…" dots until true).
export function isReady(key: Key): boolean {
  return completed.has(key);
}

// Fire-and-forget bundle — start everything the lobby and HUD will need
// while the title screen animates. Safe to call multiple times.
export function startIntroPrefetch(): void {
  preload('lobby', () => import('../lobby/cosmetic_shop'));
  preload('lobby', () => import('../lobby/gacha'));
  preload('lobby', () => import('../lobby/leaderboards'));
  preload('lobby', () => import('../lobby/matchmaker_house'));
  preload('ui-heavy', () => import('../ui/level_up_modal'));
  preload('ui-heavy', () => import('../ui/settings_modal'));
  preload('ui-heavy', () => import('../ui/screens'));
  preload('recap', () => import('../ui/death_recap'));
  preload('match-heavy', () => import('../match/powerups'));
  preload('match-heavy', () => import('../match/bosses'));
}

// For telemetry / debug overlays — exposes the current registry.
export function _debugState(): { started: Key[]; done: Key[] } {
  return {
    started: [...registry.keys()],
    done: [...completed],
  };
}
